import React from 'react'
import { t } from '../../../i18n'
import Icon from '../../shared/icon'
import { SessionPayload } from '../types'
import { isOperatorSession, sessionBlockedText } from '../sessionTypes'
import ActionButton from './ActionButton'

const scope = 'controls'

const buttonLayoutClassName =
  'btn w-100 h-100 d-inline-flex align-items-center justify-content-center gap-2 controls-actions-btn-tall'

const Spinner = () => (
  <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
)

interface Props {
  session: SessionPayload
  countdown: number
  busy: boolean
  onStart: () => void
  onFinish: () => void
}

const SessionButton = ({ session, countdown, busy, onStart, onFinish }: Props) => {
  // The type check comes before the state branches on purpose: a *pending* cleaning session
  // would otherwise render the cancel/countdown UI for a session this page cannot drive.
  if (session.state && !isOperatorSession(session)) {
    return (
      <ActionButton
        buttonClassName={`${buttonLayoutClassName} btn-primary`}
        disabled
        tooltip={sessionBlockedText(session.service_type)}
        icon={<Icon name="play" color="currentColor" />}
        label={t('start_session', { scope })}
      />
    )
  }

  if (session.state === 'pending') {
    return (
      <ActionButton
        buttonClassName={`${buttonLayoutClassName} btn-warning`}
        onClick={onFinish}
        disabled={busy}
        icon={<Spinner />}
        label={
          busy
            ? t('canceling_session', { scope })
            : `${t('cancel', { scope })} · ${t('retrying_in', { scope, seconds: countdown })}`
        }
      />
    )
  }

  if (session.state === 'active') {
    return (
      <ActionButton
        buttonClassName={`${buttonLayoutClassName} btn-danger`}
        onClick={onFinish}
        busy={busy}
        icon={<Icon name="circleStop" color="currentColor" />}
        label={t('end_session', { scope })}
      />
    )
  }

  return (
    <ActionButton
      buttonClassName={`${buttonLayoutClassName} btn-primary`}
      onClick={onStart}
      busy={busy}
      icon={<Icon name="play" color="currentColor" />}
      label={t('start_session', { scope })}
    />
  )
}

export default SessionButton
